/**
 * Product buy box: bundle tiers, per-unit variant pickers, and add-on upsells.
 * Works with pt-cart.js for AJAX add-to-cart (including multi-item payloads).
 */
(() => {
  const formatMoney = (cents, format) => {
    const fmt = format || window.Shopify?.money_format || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    const amountComma = amount.replace('.', ',');
    const amountNoDecimalsComma = amountNoDecimals;

    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimalsComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  };

  const normalizePriceEnding = ({
    rawCents,
    baseSubtotalCents,
    psychologicalPricing = '99',
    normalizationDirection = 'preserve_discount',
  }) => {
    const raw = Number.isFinite(rawCents) ? Math.round(rawCents) : 0;
    const baseSubtotal = Number.isFinite(baseSubtotalCents) ? Math.round(baseSubtotalCents) : raw;

    if (
      !psychologicalPricing ||
      psychologicalPricing === 'off' ||
      raw <= 0 ||
      baseSubtotal <= 0
    ) {
      return Math.max(0, Math.min(baseSubtotal, raw));
    }

    const targetEnding = Number(psychologicalPricing);
    if (!Number.isFinite(targetEnding) || targetEnding < 0 || targetEnding >= 100) {
      return Math.max(0, Math.min(baseSubtotal, raw));
    }

    const floorDollars = Math.floor(raw / 100);
    const candidate1 = floorDollars * 100 + targetEnding;
    const lower = candidate1 <= raw ? candidate1 : (floorDollars - 1) * 100 + targetEnding;
    const upper = candidate1 <= raw ? (floorDollars + 1) * 100 + targetEnding : candidate1;

    let selected = raw;
    if (normalizationDirection === 'preserve_discount') {
      selected = lower >= 0 ? lower : raw;
    } else if (normalizationDirection === 'round_up') {
      if (upper <= baseSubtotal) {
        selected = upper;
      } else if (lower >= 0) {
        selected = lower;
      } else {
        selected = raw;
      }
    } else if (normalizationDirection === 'nearest') {
      if (upper > baseSubtotal) {
        selected = lower >= 0 ? lower : raw;
      } else if (lower < 0) {
        selected = upper <= baseSubtotal ? upper : raw;
      } else {
        const diffLower = Math.abs(raw - lower);
        const diffUpper = Math.abs(upper - raw);
        selected = diffLower <= diffUpper ? lower : upper;
      }
    } else {
      selected = lower >= 0 ? lower : raw;
    }

    return Math.max(0, Math.min(baseSubtotal, selected));
  };

  const renderTemplateTokens = (template, tokens = {}) => {
    if (!template || typeof template !== 'string') return '';
    return template
      .replace(/\[quantity\]/g, String(tokens.quantity ?? ''))
      .replace(/\[final_price\]/g, String(tokens.finalPrice ?? ''))
      .replace(/\[amount_saved\]/g, String(tokens.amountSaved ?? ''))
      .replace(/\[percent_saved\]/g, String(tokens.percentSaved ?? ''))
      .replace(/\[price_each\]/g, String(tokens.priceEach ?? ''));
  };

  const calculateTierTotals = ({
    tierType = 'quantity',
    cartQuantity = 1,
    paidQuantity,
    percentageOff,
    percentageLeft,
    fixedDiscount = 0,
    variantPrice = 0,
    compareUnit,
    baseSubtotalCents,
    compareSubtotalCents,
    psychologicalPricing = '99',
    normalizationDirection = 'preserve_discount',
    unitPrices,
  }) => {
    const cartQty = Math.max(1, Number(cartQuantity) || 1);
    const paidQty = Math.min(cartQty, Math.max(1, Number(paidQuantity) || cartQty));

    let baseSubtotal = Number(baseSubtotalCents);
    if (!Number.isFinite(baseSubtotal)) {
      if (Array.isArray(unitPrices) && unitPrices.length) {
        baseSubtotal = unitPrices.reduce((sum, p) => sum + (Number(p) || 0), 0);
      } else {
        baseSubtotal = (Number(variantPrice) || 0) * cartQty;
      }
    }
    baseSubtotal = Math.max(0, Math.round(baseSubtotal));

    let compareSubtotal = Number(compareSubtotalCents);
    if (!Number.isFinite(compareSubtotal)) {
      const compUnit = Number(compareUnit) || Number(variantPrice) || 0;
      compareSubtotal = compUnit * cartQty;
    }
    compareSubtotal = Math.max(0, Math.round(compareSubtotal));

    let pctLeft = Number(percentageLeft);
    if (!Number.isFinite(pctLeft)) {
      const parsedPctOff = Number(percentageOff);
      pctLeft = Number.isFinite(parsedPctOff) ? (100 - parsedPctOff) / 100 : 1;
    }
    pctLeft = Math.max(0, Math.min(1, pctLeft));

    const fixedOff = Math.max(0, Math.round(Number(fixedDiscount) || 0));

    let rawPrice;
    if (tierType === 'bogo') {
      if (Array.isArray(unitPrices) && unitPrices.length) {
        const sorted = [...unitPrices].map((p) => Math.max(0, Number(p) || 0)).sort((a, b) => b - a);
        const paidSum = sorted.slice(0, paidQty).reduce((sum, p) => sum + p, 0);
        rawPrice = Math.round(paidSum * pctLeft - fixedOff);
      } else {
        rawPrice = Math.round((Number(variantPrice) || 0) * paidQty * pctLeft - fixedOff);
      }
    } else {
      rawPrice = Math.round(baseSubtotal * pctLeft - fixedOff);
    }
    rawPrice = Math.max(0, Math.min(baseSubtotal, rawPrice));

    const finalTierPriceCents = normalizePriceEnding({
      rawCents: rawPrice,
      baseSubtotalCents: baseSubtotal,
      psychologicalPricing,
      normalizationDirection,
    });

    const savings = Math.max(0, compareSubtotal - finalTierPriceCents);
    const baseSavings = Math.max(0, baseSubtotal - finalTierPriceCents);
    const actualDiscountPercent = baseSubtotal > 0 ? (baseSavings / baseSubtotal) * 100 : 0;
    const percentSaved = Math.round(actualDiscountPercent);
    const unitPrice = cartQty > 0 ? Math.round(finalTierPriceCents / cartQty) : finalTierPriceCents;

    return {
      price: finalTierPriceCents,
      rawPrice,
      compare: compareSubtotal,
      savings,
      baseSubtotal,
      actualDiscountPercent,
      percentSaved,
      unitPrice,
    };
  };

  const normalizeVariantSelections = (variants, selections) => {
    const availableVariants = variants.filter((variant) => variant.available);
    if (!availableVariants.length) {
      return { selections: [], variant: null, enabledValuesByPosition: [] };
    }

    const optionCount = selections.length || availableVariants[0].options.length;
    const normalized = [];
    const enabledValuesByPosition = [];

    for (let position = 0; position < optionCount; position += 1) {
      const candidates = availableVariants.filter((variant) =>
        normalized.every((value, index) => variant.options[index] === value),
      );
      const enabledValues = [...new Set(candidates.map((variant) => variant.options[position]))];
      const requestedValue = selections[position];
      const selectedValue = enabledValues.includes(requestedValue) ? requestedValue : enabledValues[0];

      if (selectedValue === undefined) {
        return { selections: [], variant: null, enabledValuesByPosition: [] };
      }

      enabledValuesByPosition.push(enabledValues);
      normalized.push(selectedValue);
    }

    const variant = availableVariants.find((candidate) =>
      normalized.every((value, index) => candidate.options[index] === value),
    ) ?? null;

    return { selections: normalized, variant, enabledValuesByPosition };
  };

  const parseVariants = (root) => {
    const json = root.querySelector('[data-pt-product-variants]');
    if (!json) return [];
    try {
      return JSON.parse(json.textContent);
    } catch {
      return [];
    }
  };

  const findAvailableVariant = (variants, selections) => {
    return variants.find((variant) =>
      variant.available &&
      selections.every((value, index) => variant.options[index] === value),
    );
  };

  const buildBundleItems = ({ cartQuantity, selectionsByUnit, variants, fallbackVariantId }) => {
    if (selectionsByUnit.length) {
      const items = [];
      for (const selections of selectionsByUnit) {
        const variant = findAvailableVariant(variants, selections);
        if (!variant?.id) return null;
        items.push({ id: variant.id, quantity: 1 });
      }
      return items;
    }

    const variantId = Number(fallbackVariantId);
    if (!variantId) return null;
    return [{ id: variantId, quantity: Math.max(1, Number(cartQuantity) || 1) }];
  };

  const resolveBundleVariantId = (globalVariantId, defaultVariantId) => {
    const variantId = Number(globalVariantId || defaultVariantId);
    return variantId || null;
  };

  const getBundleSubmitState = ({ baseDisabled, initialized, valid }) => ({
    disabled: Boolean(baseDisabled || !initialized || !valid),
    pending: !initialized,
  });

  const showFormStatus = (form, message) => {
    const status = form.querySelector('[data-pt-form-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  };

  const getMainVariantId = (form) => {
    const idInput = form.querySelector('[name="id"]');
    if (idInput instanceof HTMLInputElement && idInput.value) return Number(idInput.value);
    if (idInput instanceof HTMLSelectElement && idInput.value) return Number(idInput.value);
    return null;
  };

  const getSelectedTierOption = (bundleRoot) =>
    bundleRoot?.querySelector('[data-pt-bundle-input]:checked')?.closest('[data-pt-bundle-option]') ?? null;

  const getBundleVariantId = (bundleRoot) => {
    const globalVariant = bundleRoot.querySelector('[data-pt-bundle-global-variant]');
    return resolveBundleVariantId(globalVariant?.value, bundleRoot.dataset.defaultVariantId);
  };

  const getRowSelections = (row) => {
    const selections = [];
    const selects = row.querySelectorAll('[data-pt-bundle-variant-select]');
    const swatchRows = row.querySelectorAll('[data-pt-bundle-swatch-row]');

    const byPosition = new Map();
    selects.forEach((select) => {
      byPosition.set(Number(select.dataset.optionPosition), select.value);
    });
    swatchRows.forEach((swatchRow) => {
      const active = swatchRow.querySelector('[data-pt-bundle-swatch-btn][aria-pressed="true"]');
      if (active) byPosition.set(Number(swatchRow.dataset.optionPosition), active.dataset.value);
    });

    const positions = Array.from(byPosition.keys()).sort((a, b) => a - b);
    positions.forEach((pos) => selections.push(byPosition.get(pos)));
    return selections;
  };

  const getTierVariantIds = (tierEl, variants, fallbackVariantId) => {
    const cartQty = Number(tierEl.dataset.cartQuantity) || 1;
    const rows = tierEl.querySelectorAll('[data-pt-variant-row]');
    const selectionsByUnit = Array.from(rows, (row) => getRowSelections(row));

    return buildBundleItems({
      cartQuantity: cartQty,
      selectionsByUnit,
      variants,
      fallbackVariantId,
    });
  };

  const getAddonItem = (sectionRoot) => {
    const checkbox = sectionRoot.querySelector('[data-pt-addon-checkbox]:checked');
    if (!checkbox) return null;
    const variantId = Number(checkbox.value || checkbox.closest('[data-pt-addon-upsell]')?.dataset.addonVariantId);
    if (!variantId) return null;
    return { id: variantId, quantity: 1 };
  };

  const buildCartItems = (form) => {
    const sectionRoot = form.closest('.pt-product-info');
    const bundleRoot = sectionRoot?.querySelector('pt-bundle-deals');

    let items = [];

    if (bundleRoot) {
      const tierEl = getSelectedTierOption(bundleRoot);
      if (!tierEl) return null;
      const variants = parseVariants(bundleRoot);
      const fallbackVariantId = getBundleVariantId(bundleRoot);
      items = getTierVariantIds(tierEl, variants, fallbackVariantId);
      if (!items?.length) return null;
    } else {
      const fallbackVariantId = getMainVariantId(form);
      if (!fallbackVariantId) return null;
      const qtyInput = sectionRoot?.querySelector('[data-pt-qty-input]');
      const quantity = qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;
      items = [{ id: fallbackVariantId, quantity }];
    }

    const addon = getAddonItem(sectionRoot);
    if (addon) items.push(addon);

    return items;
  };

  const updateTierPrices = (bundleRoot, variantPrice, variantCompare) => {
    if (!bundleRoot || bundleRoot.dataset.updatePrices === 'false') return;

    const format = bundleRoot.dataset.moneyFormat;

    bundleRoot.querySelectorAll('[data-tier]').forEach((tierEl) => {
      const tierType = tierEl.dataset.tierType || 'quantity';
      const cartQty = Math.max(1, Number(tierEl.dataset.cartQuantity) || 1);
      const paidQty = Math.min(cartQty, Math.max(1, Number(tierEl.dataset.paidQuantity) || cartQty));
      const pctOff = Number(tierEl.dataset.percentageOff) || 0;
      const parsedPercentageLeft = Number(tierEl.dataset.percentageLeft);
      const percentageLeft = Number.isFinite(parsedPercentageLeft) ? parsedPercentageLeft : (100 - pctOff) / 100;
      const fixedDiscount = Number(tierEl.dataset.fixedDiscount) || 0;
      const compareMode = tierEl.dataset.compareMode || 'price';
      const psychologicalPricing = tierEl.dataset.psychologicalPricing || '99';
      const normalizationDirection = tierEl.dataset.normalizationDirection || 'preserve_discount';

      const rows = Array.from(tierEl.querySelectorAll('[data-pt-variant-row]'));
      let baseSubtotalCents;
      let compareSubtotalCents;
      let unitPrices;

      if (rows.length) {
        unitPrices = rows.map((row) => Number(row.dataset.variantPrice) || Number(variantPrice) || 0);
        const unitComparePrices = rows.map(
          (row) => Number(row.dataset.variantCompareAt) || Number(variantCompare) || Number(row.dataset.variantPrice) || 0,
        );
        baseSubtotalCents = unitPrices.reduce((sum, p) => sum + p, 0);
        compareSubtotalCents = unitComparePrices.reduce((sum, c, i) => {
          const uPrice = unitPrices[i] || 0;
          return sum + (compareMode === 'compare_at' && c > uPrice ? c : uPrice);
        }, 0);
      } else {
        const fallbackPrice = Number(variantPrice) || 0;
        const fallbackCompare = Number(variantCompare) || fallbackPrice;
        baseSubtotalCents = fallbackPrice * cartQty;
        const compUnit = compareMode === 'compare_at' && fallbackCompare > fallbackPrice ? fallbackCompare : fallbackPrice;
        compareSubtotalCents = compUnit * cartQty;
      }

      const totals = calculateTierTotals({
        tierType,
        cartQuantity: cartQty,
        paidQuantity: paidQty,
        percentageOff: pctOff,
        percentageLeft,
        fixedDiscount,
        variantPrice,
        baseSubtotalCents,
        compareSubtotalCents,
        psychologicalPricing,
        normalizationDirection,
        unitPrices,
      });

      const priceEl = tierEl.querySelector('[data-pt-bundle-price]');
      const compareEl = tierEl.querySelector('[data-pt-bundle-compare]');
      const savingsEl = tierEl.querySelector('[data-pt-bundle-savings]');
      const titleEl = tierEl.querySelector('[data-pt-bundle-title]');
      const saveBadgeEl = tierEl.querySelector('[data-pt-bundle-save-badge]');
      const ribbonEl = tierEl.querySelector('[data-pt-bundle-ribbon]');

      if (priceEl) priceEl.textContent = formatMoney(totals.price, format);
      if (compareEl) {
        if (totals.compare > totals.price) {
          compareEl.textContent = formatMoney(totals.compare, format);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      const tokens = {
        quantity: cartQty,
        finalPrice: formatMoney(totals.price, format),
        amountSaved: formatMoney(totals.savings, format),
        percentSaved: totals.percentSaved,
        priceEach: formatMoney(totals.unitPrice, format),
      };

      if (savingsEl) {
        const template = savingsEl.dataset.savingsTemplate || tierEl.dataset.savingsTemplate || '';
        if (template) {
          savingsEl.textContent = renderTemplateTokens(template, tokens);
        }
      }

      if (titleEl) {
        const titleTemplate = tierEl.dataset.labelTemplate;
        if (titleTemplate && titleTemplate.includes('[')) {
          titleEl.textContent = renderTemplateTokens(titleTemplate, tokens);
        }
      }

      if (saveBadgeEl) {
        const badgeTemplate = tierEl.dataset.saveBadgeTemplate;
        if (badgeTemplate && badgeTemplate.includes('[')) {
          saveBadgeEl.textContent = renderTemplateTokens(badgeTemplate, tokens);
        }
      }

      if (ribbonEl) {
        const ribbonTemplate = tierEl.dataset.badgeTemplate;
        if (ribbonTemplate && ribbonTemplate.includes('[')) {
          ribbonEl.textContent = renderTemplateTokens(ribbonTemplate, tokens);
        }
      }
    });
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildBundleItems,
      calculateTierTotals,
      getBundleSubmitState,
      getSelectedTierOption,
      normalizePriceEnding,
      normalizeVariantSelections,
      renderTemplateTokens,
      resolveBundleVariantId,
    };
  }

  if (typeof document === 'undefined') return;

  const syncVariantRow = (row, variants) => {
    const selects = Array.from(row.querySelectorAll('[data-pt-bundle-variant-select]'));
    const swatchRows = Array.from(row.querySelectorAll('[data-pt-bundle-swatch-row]'));
    const currentSelections = getRowSelections(row);
    const normalized = normalizeVariantSelections(variants, currentSelections);

    let posIndex = 0;
    selects.forEach((select) => {
      const pos = Number(select.dataset.optionPosition);
      const idx = pos - 1;
      const enabledValues = new Set(normalized.enabledValuesByPosition[idx] ?? []);
      Array.from(select.options).forEach((option) => {
        option.disabled = !enabledValues.has(option.value);
      });
      if (normalized.selections[idx] !== undefined) {
        select.value = normalized.selections[idx];
      }
    });

    swatchRows.forEach((swatchRow) => {
      const pos = Number(swatchRow.dataset.optionPosition);
      const idx = pos - 1;
      const enabledValues = new Set(normalized.enabledValuesByPosition[idx] ?? []);
      swatchRow.querySelectorAll('[data-pt-bundle-swatch-btn]').forEach((btn) => {
        const available = enabledValues.has(btn.dataset.value);
        btn.disabled = !available;
        btn.classList.toggle('is-unavailable', !available);
      });
      if (normalized.selections[idx] !== undefined) {
        swatchRow.querySelectorAll('[data-pt-bundle-swatch-btn]').forEach((btn) => {
          const isActive = btn.dataset.value === normalized.selections[idx];
          btn.setAttribute('aria-pressed', String(isActive));
          btn.classList.toggle('is-active', isActive);
        });
        const labelEl = swatchRow.parentElement?.querySelector(`[data-pt-selected-val][data-option-position="${pos}"]`);
        if (labelEl) {
          labelEl.textContent = normalized.selections[idx];
        }
      }
    });

    row.dataset.variantValid = String(Boolean(normalized.variant));
    row.dataset.variantId = normalized.variant?.id ? String(normalized.variant.id) : '';
    row.dataset.variantPrice = normalized.variant?.price !== undefined ? String(normalized.variant.price) : '';
    row.dataset.variantCompareAt = normalized.variant?.compare_at_price !== undefined ? String(normalized.variant.compare_at_price) : '';
    return normalized.variant;
  };

  const syncBundleValidity = (bundleRoot, form) => {
    const selectedTier = getSelectedTierOption(bundleRoot);
    const rows = selectedTier ? Array.from(selectedTier.querySelectorAll('[data-pt-variant-row]')) : [];
    const fallbackInvalid = rows.length === 0 && !getBundleVariantId(bundleRoot);
    const invalid = !selectedTier || fallbackInvalid || rows.some((row) => row.dataset.variantValid !== 'true');
    const submit = form.querySelector('[type="submit"]');

    if (submit instanceof HTMLButtonElement) {
      const state = getBundleSubmitState({
        baseDisabled: submit.dataset.ptBaseDisabled === 'true',
        initialized: bundleRoot.dataset.ptControllerInitialized === 'true',
        valid: !invalid,
      });
      submit.dataset.ptBundleInvalid = String(invalid);
      submit.disabled = state.disabled;
      submit.toggleAttribute('data-pt-bundle-pending', state.pending);
    }

    showFormStatus(form, invalid ? bundleRoot.dataset.invalidSelectionMessage : '');
    return !invalid;
  };

  const updatePriceBlock = (priceBlock, priceCents, compareCents) => {
    if (!priceBlock || !Number.isFinite(priceCents)) return;

    const currentEl = priceBlock.querySelector('[data-pt-price-current]');
    const compareEl = priceBlock.querySelector('[data-pt-price-compare]');
    const badgeEl = priceBlock.querySelector('[data-pt-price-badge]');
    const installmentEl = priceBlock.querySelector('[data-pt-price-installment-val]');

    const moneyFormat = window.Shopify?.money_format || '$' + '{{amount}}';

    if (currentEl) {
      currentEl.textContent = formatMoney(priceCents, moneyFormat);
    }

    const showCompare = priceBlock.dataset.showCompare !== 'false';
    const showSavings = priceBlock.dataset.showSavings !== 'false';
    const badgeType = priceBlock.dataset.badgeType || 'percent';
    const customText = (priceBlock.dataset.badgeCustomText || '').trim();
    const overrideText = priceBlock.dataset.compareOverride;

    const hasCompare = (compareCents && compareCents > priceCents) || Boolean(overrideText);

    if (compareEl && showCompare) {
      if (compareCents && compareCents > priceCents) {
        compareEl.textContent = formatMoney(compareCents, moneyFormat);
        compareEl.style.display = '';
      } else if (overrideText) {
        compareEl.textContent = overrideText;
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    } else if (compareEl) {
      compareEl.style.display = 'none';
    }

    if (badgeEl && showSavings && hasCompare) {
      if (customText) {
        badgeEl.textContent = customText;
        badgeEl.style.display = '';
      } else if (badgeType === 'amount' && compareCents && compareCents > priceCents) {
        const diff = compareCents - priceCents;
        badgeEl.textContent = `Save ${formatMoney(diff, moneyFormat)}`;
        badgeEl.style.display = '';
      } else if (compareCents && compareCents > priceCents) {
        const diff = compareCents - priceCents;
        const pct = Math.round((diff * 100) / compareCents);
        badgeEl.textContent = `Save ${pct}%`;
        badgeEl.style.display = '';
      } else {
        badgeEl.textContent = 'SALE';
        badgeEl.style.display = '';
      }
    } else if (badgeEl) {
      badgeEl.style.display = 'none';
    }

    if (installmentEl) {
      installmentEl.textContent = formatMoney(Math.round(priceCents / 4), moneyFormat);
    }
  };

  const bindBundleDeals = (bundleRoot, form) => {
    const variants = parseVariants(bundleRoot);
    const defaultVariant = variants.find((v) => String(v.id) === String(bundleRoot.dataset.defaultVariantId)) || variants[0];

    const syncPrices = () => {
      const select = bundleRoot.querySelector('[data-pt-bundle-global-variant]');
      let activeVariant = defaultVariant;
      if (select instanceof HTMLSelectElement) {
        activeVariant = variants.find((v) => String(v.id) === select.value) || defaultVariant;
      }
      const price = activeVariant?.price;
      const compare = activeVariant?.compare_at_price > activeVariant?.price ? activeVariant.compare_at_price : activeVariant?.price;
      updateTierPrices(bundleRoot, price, compare);
      const sectionContainer = bundleRoot.closest('.pt-product-section') || bundleRoot.closest('.pt-product-info');
      const priceBlock = sectionContainer?.querySelector('[data-price-block]');
      if (priceBlock && price !== undefined) {
        updatePriceBlock(priceBlock, price, compare);
      }
    };

    const inputs = bundleRoot.querySelectorAll('[data-pt-bundle-input]');
    if (inputs.length && !bundleRoot.querySelector('[data-pt-bundle-input]:checked')) {
      inputs[0].checked = true;
    }

    const syncSelection = () => {
      bundleRoot.querySelectorAll('[data-pt-bundle-card]').forEach((card) => {
        const inputId = card.getAttribute('for');
        const input = inputId ? document.getElementById(inputId) : null;
        card.classList.toggle('is-selected', Boolean(input?.checked));
      });

      const isAlwaysVisible = bundleRoot.dataset.variantVisibility === 'always_visible';
      const selectedTier = getSelectedTierOption(bundleRoot);
      bundleRoot.querySelectorAll('[data-pt-bundle-variants]').forEach((panel) => {
        panel.hidden = !isAlwaysVisible && (!selectedTier || panel.dataset.tier !== selectedTier.dataset.tier);
      });

      syncBundleValidity(bundleRoot, form);
    };

    bundleRoot.querySelectorAll('[data-pt-bundle-input]').forEach((input) => {
      input.addEventListener('change', syncSelection);
    });

    bundleRoot.querySelectorAll('[data-pt-variant-row]').forEach((row) => {
      syncVariantRow(row, variants);
      row.querySelectorAll('[data-pt-bundle-variant-select]').forEach((select) => {
        select.addEventListener('change', () => {
          syncVariantRow(row, variants);
          syncPrices();
          syncBundleValidity(bundleRoot, form);
        });
      });
      row.querySelectorAll('[data-pt-bundle-swatch-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          const swatchRow = btn.closest('[data-pt-bundle-swatch-row]');
          swatchRow.querySelectorAll('[data-pt-bundle-swatch-btn]').forEach((sibling) => {
            sibling.setAttribute('aria-pressed', 'false');
            sibling.classList.remove('is-active');
          });
          btn.setAttribute('aria-pressed', 'true');
          btn.classList.add('is-active');
          syncVariantRow(row, variants);
          syncPrices();
          syncBundleValidity(bundleRoot, form);
        });
      });
    });

    bundleRoot.dataset.ptControllerInitialized = 'true';
    syncPrices();
    syncSelection();

    const onVariantChange = () => {
      syncPrices();
    };

    bundleRoot.querySelector('[data-pt-bundle-global-variant]')?.addEventListener('change', onVariantChange);
  };

  const bindQuantityStepper = (root) => {
    root.querySelectorAll('[data-pt-qty-minus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement?.querySelector('[data-pt-qty-input]');
        if (!(input instanceof HTMLInputElement)) return;
        input.value = String(Math.max(1, (Number(input.value) || 1) - 1));
      });
    });
    root.querySelectorAll('[data-pt-qty-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement?.querySelector('[data-pt-qty-input]');
        if (!(input instanceof HTMLInputElement)) return;
        input.value = String((Number(input.value) || 1) + 1);
      });
    });
  };

  const initProductForm = (form) => {
    const sectionRoot = form.closest('.pt-product-info') || form.closest('.pt-product-section');
    const bundleRoot = sectionRoot?.querySelector('pt-bundle-deals');
    const priceBlock = sectionRoot?.querySelector('[data-price-block]');
    const submit = form.querySelector('[type="submit"]');
    if (submit instanceof HTMLButtonElement) {
      if (submit.dataset.ptBaseDisabled === undefined) {
        submit.dataset.ptBaseDisabled = String(submit.disabled);
      }
    }
    if (bundleRoot) bindBundleDeals(bundleRoot, form);
    if (sectionRoot) bindQuantityStepper(sectionRoot);

    const variantSelect = form.querySelector('[data-pt-variant-select]');
    if (variantSelect instanceof HTMLSelectElement) {
      const onSelectChange = () => {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        if (selectedOption) {
          const price = Number(selectedOption.dataset.price);
          const compareAt = Number(selectedOption.dataset.compareAt) || 0;
          if (priceBlock) updatePriceBlock(priceBlock, price, compareAt);
        }
      };
      variantSelect.addEventListener('change', onSelectChange);
    }

    form.addEventListener('submit', (event) => {
      const items = buildCartItems(form);
      if (!items?.length) {
        if (bundleRoot) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showFormStatus(form, bundleRoot.dataset.invalidSelectionMessage);
        }
        return;
      }

      const isMulti =
        items.length > 1 ||
        items[0].quantity > 1 ||
        Boolean(sectionRoot?.querySelector('[data-pt-addon-checkbox]:checked'));

      if (!isMulti && !bundleRoot) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      showFormStatus(form, '');

      form.dispatchEvent(
        new CustomEvent('pt:cart-add', {
          bubbles: true,
          detail: { form, items },
        }),
      );
    }, true);
  };

  document.querySelectorAll('[data-pt-product-form]').forEach((form) => {
    if (form instanceof HTMLFormElement) initProductForm(form);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-pt-product-form]').forEach((form) => {
      if (form instanceof HTMLFormElement) initProductForm(form);
    });
  });
})();
